import { Injectable } from '@angular/core';
import {
  enumMarineInsuranceChangesType,
  enumMarineInsuranceCommodity,
  enumMarineInsurancePendingPaymentAction,
} from '@app/data-layer/enums/marine-insurance';
import { MarineInsuranceManagementModel } from '@data-layer/models/marine-insurance';
import { enumBillItemProductCode, enumPaymentState } from '@data-layer/enums/payments';
import { BillItemPendingModel } from '@data-layer/models/payments';

@Injectable({ providedIn: 'root' })
export class MarineInsuranceHelperService {
  constructor() {}

  public isShowChangesWarning(marineInsurance: MarineInsuranceManagementModel): boolean {
    return (
      this.isShowBLChangedWarning(marineInsurance) ||
      this.isShowDetailsChangedWarning(marineInsurance) ||
      this.isShowAdditionalPaymentWarning(marineInsurance) ||
      this.isShowRefundWarning(marineInsurance)
    );
  }

  public isShowBLChangedWarning(marineInsurance: MarineInsuranceManagementModel): boolean {
    return marineInsurance?.changesType === enumMarineInsuranceChangesType.BILL_NUMBER_CHANGED;
  }

  public isShowDetailsChangedWarning(marineInsurance: MarineInsuranceManagementModel): boolean {
    return [enumMarineInsuranceChangesType.DETAILS_CHANGED, enumMarineInsuranceChangesType.ALL].includes(marineInsurance?.changesType);
  }

  public isShowAdditionalPaymentWarning(marineInsurance: MarineInsuranceManagementModel): boolean {
    return (
      marineInsurance?.pendingPaymentAction === enumMarineInsurancePendingPaymentAction.ADDITIONAL_PAYMENT &&
      marineInsurance?.updatedPaymentState === enumPaymentState.SUCCESS
    );
  }

  public isShowRefundWarning(marineInsurance: MarineInsuranceManagementModel): boolean {
    return !!marineInsurance?.partiallyRefundConfirmed && !!marineInsurance?.pendingPaymentAction;
  }

  public warningText(marineInsurance: MarineInsuranceManagementModel): string {
    if (this.isShowBLChangedWarning(marineInsurance)) {
      return 'MARINE_INSURANCE_BL_HAVE_BEEN_UPDATED_BY_USER_HINT';
    }

    if (this.isShowDetailsChangedWarning(marineInsurance)) {
      return 'MARINE_INSURANCE_DETAILS_HAVE_BEEN_UPDATED_BY_USER_HINT';
    }

    if (this.isShowAdditionalPaymentWarning(marineInsurance)) {
      return 'MARINE_INSURANCE_ADDITIONAL_PAYMENT_ADDED_HINT';
    }

    if (this.isShowRefundWarning(marineInsurance)) {
      return 'MARINE_INSURANCE_REFUND_ADDED_HINT';
    }
  }

  public getCommodityDescription(commodityId: number): string {
    switch (commodityId) {
      case enumMarineInsuranceCommodity.GENERAL:
        return 'MARINE_INSURANCE_COMMODITY_DESCRIPTION_GENERAL';

      case enumMarineInsuranceCommodity.ELECTRONICS:
        return 'MARINE_INSURANCE_COMMODITY_DESCRIPTION_ELECTRONICS';

      case enumMarineInsuranceCommodity.VEHICLES:
        return 'MARINE_INSURANCE_COMMODITY_DESCRIPTION_VEHICLES';

      case enumMarineInsuranceCommodity.MOBILE_PHONES:
        return 'MARINE_INSURANCE_COMMODITY_DESCRIPTION_MOBILE_PHONES';

      case enumMarineInsuranceCommodity.FRAGILES:
        return 'MARINE_INSURANCE_COMMODITY_DESCRIPTION_FRAGILE';

      default:
        return 'MARINE_INSURANCE_COMMODITY_DESCRIPTION_GENERAL';
    }
  }

  public getJoinedItems(pendingBillsItems: BillItemPendingModel[]): BillItemPendingModel[] {
    const nonCollectionFeeItems: BillItemPendingModel[] = (pendingBillsItems || []).filter(
      (paymentItem: BillItemPendingModel) => ![enumBillItemProductCode.COLLECTION_FEE].includes(paymentItem.productCode),
    );
    const collectionFeeItems: BillItemPendingModel[] =
      (pendingBillsItems || []).filter(
        (paymentItem: BillItemPendingModel) => paymentItem.productCode === enumBillItemProductCode.COLLECTION_FEE,
      ) || [];

    const result: BillItemPendingModel[] = [...nonCollectionFeeItems];
    if (collectionFeeItems?.length) {
      result.push({
        ...collectionFeeItems[0],
        quantity: 1,
        price: collectionFeeItems.reduce((acc: number, curr: BillItemPendingModel) => acc + curr.price, 0),
      });
    }

    return result;
  }
}
